import { useId } from 'react';

/*
 * Filled, coloured glyphs and small illustrations drawn to match the Counsel
 * and Growth product mockups (Health scores 09.57.34, Recommendations
 * 10.03.13, Trends 09.45.09, Analytics 10.06.42). The lucide set is
 * outline-only, so these are hand-drawn on a 48-unit grid. Every glyph is
 * decorative: aria-hidden, focusable="false", always next to visible text.
 * Single-colour glyphs use `currentColor`; multi-colour ones carry their own.
 */

const base = (size, className, viewBox = '0 0 48 48') => ({
  width: size,
  height: size,
  viewBox,
  className,
  'aria-hidden': true,
  focusable: 'false',
});

/* ---------- Health scores: the five method tiles ---------- */

/* An upright test tube with red liquid in its lower half. */
export function TestTubeGlyph({ size = 30, className }) {
  return (
    <svg {...base(size, className)}>
      <path d="M17.5 24h13v10.5a6.5 6.5 0 0 1-13 0Z" fill="currentColor" />
      <path
        d="M18 7.5v27a6 6 0 0 0 12 0v-27"
        fill="none"
        stroke="currentColor"
        strokeWidth="3.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M14 6.5h20" stroke="currentColor" strokeWidth="3.8" strokeLinecap="round" />
      <path d="M22 28v6" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" opacity="0.7" />
    </svg>
  );
}

/* A glossy purple liver. */
export function LiverGlyph({ size = 34, className }) {
  const g = useId();
  return (
    <svg {...base(size, className)}>
      <defs>
        <linearGradient id={g} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#a678ff" />
          <stop offset="1" stopColor="#6a2fe0" />
        </linearGradient>
      </defs>
      <path
        d="M6 17.5c5.5-6 17-8.6 30-7.6 5.4.4 7 4.4 4.6 8.6-3.8 6.8-12.4 14-22 16.6C11.4 37 6 33.6 5.2 27.4 4.8 23.6 4.8 19 6 17.5Z"
        fill={`url(#${g})`}
      />
      <path d="M12 16c6-3 14-4 21-3.4" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" fill="none" opacity="0.35" />
    </svg>
  );
}

/* Five linked nodes in an X: patterns across related results. */
export function NetworkGlyph({ size = 30, className }) {
  return (
    <svg {...base(size, className)}>
      <path d="M12 12l24 24M36 12L12 36" stroke="currentColor" strokeWidth="3.6" strokeLinecap="round" />
      <circle cx="24" cy="24" r="5" fill="currentColor" />
      <circle cx="10.5" cy="10.5" r="5.5" fill="currentColor" />
      <circle cx="37.5" cy="10.5" r="5.5" fill="currentColor" />
      <circle cx="10.5" cy="37.5" r="5.5" fill="currentColor" />
      <circle cx="37.5" cy="37.5" r="5.5" fill="currentColor" />
    </svg>
  );
}

/* Four outlined pill bars of different heights. */
export function PillBarsGlyph({ size = 32, className }) {
  const bars = [
    { x: 8, h: 18 },
    { x: 17.5, h: 30 },
    { x: 27, h: 26 },
    { x: 36.5, h: 16 },
  ];
  return (
    <svg {...base(size, className)}>
      {bars.map((b) => (
        <rect
          key={b.x}
          x={b.x - 3}
          y={41 - b.h}
          width="6"
          height={b.h}
          rx="3"
          fill="none"
          stroke="currentColor"
          strokeWidth="3.2"
        />
      ))}
    </svg>
  );
}

/* An outlined head-and-shoulders person. */
export function PersonGlyph({ size = 32, className }) {
  return (
    <svg {...base(size, className)}>
      <circle cx="24" cy="15.5" r="8" fill="none" stroke="currentColor" strokeWidth="3.4" />
      <path
        d="M9.5 41.5v-2c0-6.6 5.4-12 12-12h5c6.6 0 12 5.4 12 12v2Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="3.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ---------- Alerts and status marks ---------- */

/* A filled rounded warning triangle with a white "!". */
export function AlertGlyph({ size = 20, className, color = '#e5484d' }) {
  return (
    <svg {...base(size, className)}>
      <path
        d="M20.5 7.6a4 4 0 0 1 7 0l16.2 28.1a4 4 0 0 1-3.5 6H7.8a4 4 0 0 1-3.5-6Z"
        fill={color}
      />
      <rect x="22" y="16" width="4" height="13" rx="2" fill="#fff" />
      <circle cx="24" cy="34.5" r="2.5" fill="#fff" />
    </svg>
  );
}

/* A filled drop-shaped pin with a white dot (the "Raised ESR" mark). */
export function PinDropGlyph({ size = 20, className, color = '#f2a91f' }) {
  return (
    <svg {...base(size, className)}>
      <path d="M24 4C15.7 4 9.5 10.4 9.5 18.4 9.5 29 24 44 24 44s14.5-15 14.5-25.6C38.5 10.4 32.3 4 24 4Z" fill={color} />
      <circle cx="24" cy="18.5" r="5.5" fill="#fff" />
    </svg>
  );
}

/* A filled circle with a white tick. */
export function CheckDiscGlyph({ size = 20, className, color = '#1fa463' }) {
  return (
    <svg {...base(size, className)}>
      <circle cx="24" cy="24" r="20" fill={color} />
      <path d="M15 24.5l6 6 12-13" fill="none" stroke="#fff" strokeWidth="4.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* A blue gradient shield with a white tick (or a white sparkle). */
export function ShieldGlyph({ size = 30, className, mark = 'tick' }) {
  const g = useId();
  return (
    <svg {...base(size, className)}>
      <defs>
        <linearGradient id={g} x1="0" y1="0" x2="0.4" y2="1">
          <stop offset="0" stopColor="#3b82ff" />
          <stop offset="1" stopColor="#1145d6" />
        </linearGradient>
      </defs>
      <path d="M24 3.5l17 6.4v11.6c0 11.2-7.2 19.8-17 23.4C14.2 41.3 7 32.7 7 21.5V9.9Z" fill={`url(#${g})`} />
      {mark === 'tick' ? (
        <path
          d="M16 24l6 6 10.5-11.5"
          fill="none"
          stroke="#fff"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : (
        <path
          d="M26.5 13l-2.2 8.2 7.7 2.2-11.5 12.6 2.2-8.6-7.7-2Z"
          fill="#fff"
          stroke="#fff"
          strokeWidth="1.2"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}

/* ---------- Recommendations: column heads ---------- */

/* An Erlenmeyer flask with pale liquid. */
export function FlaskGlyph({ size = 34, className }) {
  return (
    <svg {...base(size, className)}>
      <path d="M13.2 31h21.6l4.4 7.6A3 3 0 0 1 36.6 43H11.4a3 3 0 0 1-2.6-4.4Z" fill="currentColor" opacity="0.28" />
      <path
        d="M19.5 5.5v13L8.6 38.4A3.2 3.2 0 0 0 11.4 43h25.2a3.2 3.2 0 0 0 2.8-4.6L28.5 18.5v-13"
        fill="none"
        stroke="currentColor"
        strokeWidth="3.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M16 5.5h16" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" />
    </svg>
  );
}

/* A filled fork and knife. */
export function ForkKnifeGlyph({ size = 34, className }) {
  return (
    <svg {...base(size, className)}>
      <path
        d="M10.5 5h3v10.5h2.2V5h3v10.5h2.2V5h3v12.2c0 3-1.9 5.4-4.5 6.2V42a2.8 2.8 0 0 1-5.6 0V23.4C11.2 22.6 10.5 20.2 10.5 17.2Z"
        fill="currentColor"
      />
      <path d="M37.5 5c-5.2 1-8.4 7.4-8.4 15.4 0 3.6 1.6 5.8 4.4 6.6V42a2.8 2.8 0 0 0 5.6 0V7.2c0-1.4-.6-2.4-1.6-2.2Z" fill="currentColor" />
    </svg>
  );
}

/* A doctor: outlined bust with a cap and a collar. */
export function DoctorGlyph({ size = 34, className }) {
  return (
    <svg {...base(size, className)}>
      <g fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.5 15.5c0-6 4.3-10 9.5-10s9.5 4 9.5 10" />
        <path d="M14.5 15.5h19" />
        <path d="M15.5 16.5v2.5a8.5 8.5 0 0 0 17 0v-2.5" />
        <path d="M8.5 43v-3.5c0-6.4 4.6-10.5 10.5-11.5l5 6 5-6c5.9 1 10.5 5.1 10.5 11.5V43Z" />
        <path d="M24 34v9" />
      </g>
      <rect x="21" y="7.5" width="6" height="5" rx="1.2" fill="currentColor" opacity="0.35" />
    </svg>
  );
}

/* ---------- Recommendations: diet row illustrations ---------- */

/* A glossy leafy green (spinach) leaf with a lighter leaf behind. */
export function LeafyGreensArt({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path d="M31 8c8 3 11 12 7 20-2.6 5.2-7.6 7.6-12.4 7.4C27.4 26 27 15 31 8Z" fill="#6cbf4f" />
      <path
        d="M8 40C6 28 11 15 22 10c6-2.6 11 1 10.4 7.4-.8 9.6-9.8 19.6-18.4 22.4Z"
        fill="#2f8f3a"
      />
      <path
        d="M10 41c4-8 9-16 17-24M15 33l-3-6M19 27l-2-7M15 33l7-1M19 27l7-1.5"
        stroke="#bfe6a6"
        strokeWidth="1.6"
        strokeLinecap="round"
        fill="none"
      />
      <path d="M7 43l4-3" stroke="#2f8f3a" strokeWidth="2.6" strokeLinecap="round" />
    </svg>
  );
}

/* An orange slice in front of a whole orange. */
export function OrangeSliceArt({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <circle cx="31" cy="18" r="12" fill="#f7a21b" />
      <path d="M31 6.5c1.4-2.2 3.8-3 6-2.4-1 2.4-3.4 3.6-6 2.4Z" fill="#59a83b" />
      <circle cx="20" cy="29" r="15" fill="#f58a0b" />
      <circle cx="20" cy="29" r="12.4" fill="#ffc94a" />
      <g stroke="#fff3c8" strokeWidth="1.8" strokeLinecap="round">
        <path d="M20 29V17.4M20 29l9.4-6.8M20 29l9.4 6.8M20 29v11.6M20 29l-9.4 6.8M20 29l-9.4-6.8" />
      </g>
      <circle cx="20" cy="29" r="2" fill="#fff3c8" />
    </svg>
  );
}

/* A tall glass of water. */
export function WaterGlassArt({ className }) {
  const g = useId();
  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={g} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#7cc4ff" />
          <stop offset="1" stopColor="#2e8cf0" />
        </linearGradient>
      </defs>
      <path d="M13 6h22l-3 37a2.4 2.4 0 0 1-2.4 2.2H18.4A2.4 2.4 0 0 1 16 43Z" fill="#e8f4ff" stroke="#9fcdf6" strokeWidth="1.6" />
      <path d="M14.4 17h19.2l-2.3 25.4a1.6 1.6 0 0 1-1.6 1.5H18.3a1.6 1.6 0 0 1-1.6-1.5Z" fill={`url(#${g})`} />
      <path d="M18 20l1.6 20" stroke="#fff" strokeWidth="2" strokeLinecap="round" opacity="0.55" />
    </svg>
  );
}

/* ---------- Trends ---------- */

/* A rising line with an arrowhead over a baseline ("Track over time"). */
export function TrackGlyph({ size = 34, className }) {
  return (
    <svg {...base(size, className)}>
      <g fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M7 31l10-10 7 6 15-15" />
        <path d="M30 11h9v9" />
        <path d="M7 40h34" />
      </g>
    </svg>
  );
}

/* A zig-zag rising arrow ("Spot changes early"). */
export function RiseGlyph({ size = 34, className }) {
  return (
    <svg {...base(size, className)}>
      <g fill="none" stroke="currentColor" strokeWidth="3.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 35l11-11 7 6 17-17" />
        <path d="M31 12h10v10" />
      </g>
    </svg>
  );
}

/* Three people, the front one larger ("Bring patients back"). */
export function GroupGlyph({ size = 34, className }) {
  return (
    <svg {...base(size, className)}>
      <g fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="24" cy="15" r="6" />
        <circle cx="11" cy="18" r="4.6" />
        <circle cx="37" cy="18" r="4.6" />
        <path d="M14 40v-3c0-5.5 4.5-9.5 10-9.5s10 4 10 9.5v3Z" />
        <path d="M4 37v-2c0-4.2 3-7 7-7 1.6 0 3 .4 4.2 1.2" />
        <path d="M44 37v-2c0-4.2-3-7-7-7-1.6 0-3 .4-4.2 1.2" />
      </g>
    </svg>
  );
}

/* Three filled bars rising (the Change tile, the Analytics product). */
export function BarsGlyph({ size = 22, className }) {
  return (
    <svg {...base(size, className)}>
      <rect x="7" y="25" width="8" height="17" rx="2" fill="currentColor" />
      <rect x="20" y="14" width="8" height="28" rx="2" fill="currentColor" />
      <rect x="33" y="6" width="8" height="36" rx="2" fill="currentColor" />
    </svg>
  );
}

/* A target: ring, dot and a soft halo (the Range tile). */
export function TargetGlyph({ size = 22, className }) {
  return (
    <svg {...base(size, className)}>
      <circle cx="24" cy="24" r="19" fill="currentColor" opacity="0.18" />
      <circle cx="24" cy="24" r="13" fill="none" stroke="currentColor" strokeWidth="4" />
      <circle cx="24" cy="24" r="5" fill="currentColor" />
    </svg>
  );
}

/* An outlined WhatsApp-style bubble with a handset. */
export function ChatPhoneGlyph({ size = 26, className }) {
  return (
    <svg {...base(size, className)}>
      <path
        d="M24 5.5A18.5 18.5 0 0 0 8 33.3L5.5 42.5l9.4-2.4A18.5 18.5 0 1 0 24 5.5Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="3.2"
        strokeLinejoin="round"
      />
      <path
        d="M18.4 15.2c.6-.2 1.2.1 1.4.7l1.6 3.8c.2.5.1 1-.3 1.4l-1.4 1.4c1.1 2.4 3 4.3 5.4 5.4l1.4-1.4c.4-.4 1-.5 1.4-.3l3.8 1.6c.6.2.9.8.7 1.4l-.6 2.2c-.2.8-1 1.4-1.8 1.4-7.6-.4-13.6-6.4-14-14 0-.8.6-1.6 1.4-1.8Z"
        fill="currentColor"
      />
    </svg>
  );
}

/* ---------- Analytics ---------- */

/* An orange dart target with an arrow in the bull. */
export function DartGlyph({ size = 34, className }) {
  return (
    <svg {...base(size, className)}>
      <g fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M37.5 21A15 15 0 1 1 27 9.5" />
        <path d="M30 24a6 6 0 1 1-6-6" />
        <path d="M24 24L39 9" />
        <path d="M34 9h5V4M39 9h5" />
      </g>
      <circle cx="24" cy="24" r="2.6" fill="currentColor" />
    </svg>
  );
}

/* A person with three list lines ("Who to target next?"). */
export function PersonListGlyph({ size = 34, className }) {
  return (
    <svg {...base(size, className)}>
      <g fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="19" cy="15" r="8" />
        <path d="M5 41v-2.5C5 32 10.4 27 17 27h4" />
        <path d="M30 31h13M30 37h13M30 43h9" />
      </g>
    </svg>
  );
}

/* A white envelope (for a solid blue disc). */
export function EnvelopeGlyph({ size = 18, className }) {
  return (
    <svg {...base(size, className)}>
      <rect x="6" y="11" width="36" height="26" rx="4" fill="currentColor" />
      <path d="M8 14l16 12 16-12" fill="none" stroke="#1c7cf4" strokeWidth="3" strokeLinejoin="round" />
    </svg>
  );
}

/* A headset agent desk (for a solid purple disc). */
export function AgentsGlyph({ size = 18, className }) {
  return (
    <svg {...base(size, className)}>
      <rect x="8" y="6" width="32" height="36" rx="5" fill="none" stroke="currentColor" strokeWidth="3.4" />
      <g fill="currentColor">
        <rect x="14" y="12" width="6" height="6" rx="1.4" />
        <rect x="28" y="12" width="6" height="6" rx="1.4" />
        <rect x="14" y="22" width="6" height="6" rx="1.4" />
        <rect x="28" y="22" width="6" height="6" rx="1.4" />
        <rect x="20" y="32" width="8" height="10" rx="1.4" />
      </g>
    </svg>
  );
}
